import { defineStore } from 'pinia'
import localForage from 'localforage'

const CACHE_META_KEY = 'media_cache_meta'
const MAX_CACHE_SIZE_MB = 500
const MAX_CACHE_SIZE = MAX_CACHE_SIZE_MB * 1024 * 1024
const CACHE_EXPIRE_DAYS = 30
const CACHE_EXPIRE_MS = CACHE_EXPIRE_DAYS * 24 * 60 * 60 * 1000

const IMAGE_PREFIX = 'img_cache_'
const VIDEO_PREFIX = 'video_cache_'
const COVER_PREFIX = 'cover_cache_'

export default defineStore('mediaCacheStore', {
  state: () => ({
    cacheIndex: new Map(),
    totalCacheSize: 0,
    isInitialized: false,
    pendingWrites: []
  }),

  actions: {
    async init() {
      if (this.isInitialized) return

      try {
        const meta = await localForage.getItem(CACHE_META_KEY)
        if (meta && meta.entries) {
          this.cacheIndex = new Map(meta.entries)
          this.totalCacheSize = meta.totalSize || 0
          await this.cleanExpired()
        }
        this.isInitialized = true
        console.log('[MediaCache] Initialized, entries:', this.cacheIndex.size, 'size:', this.formatSize(this.totalCacheSize))
      } catch (error) {
        console.error('[MediaCache] Init failed:', error)
        this.cacheIndex = new Map()
        this.totalCacheSize = 0
        this.isInitialized = true
      }
    },

    async setCache(key, filePath, size = 0) {
      const entry = {
        filePath,
        size,
        accessTime: Date.now(),
        createTime: Date.now()
      }
      this.cacheIndex.set(key, entry)
      this.totalCacheSize += size
      await this.debouncedSaveMeta()
      await this.cleanupIfNeeded()
    },

    getCache(key) {
      const entry = this.cacheIndex.get(key)
      if (entry) {
        entry.accessTime = Date.now()
        return entry.filePath
      }
      return null
    },

    hasCache(key) {
      return this.cacheIndex.has(key)
    },

    async setImageCache(msgId, filePath, size = 0) {
      await this.setCache(`${IMAGE_PREFIX}${msgId}`, filePath, size)
    },

    getImageCache(msgId) {
      return this.getCache(`${IMAGE_PREFIX}${msgId}`)
    },

    hasImageCache(msgId) {
      return this.hasCache(`${IMAGE_PREFIX}${msgId}`)
    },

    async setVideoCache(msgId, filePath, size = 0) {
      await this.setCache(`${VIDEO_PREFIX}${msgId}`, filePath, size)
    },

    getVideoCache(msgId) {
      return this.getCache(`${VIDEO_PREFIX}${msgId}`)
    },

    hasVideoCache(msgId) {
      return this.hasCache(`${VIDEO_PREFIX}${msgId}`)
    },

    async setCoverCache(msgId, filePath, size = 0) {
      await this.setCache(`${COVER_PREFIX}${msgId}`, filePath, size)
    },

    getCoverCache(msgId) {
      return this.getCache(`${COVER_PREFIX}${msgId}`)
    },

    async cleanExpired() {
      const now = Date.now()
      const expiredKeys = []

      for (const [key, entry] of this.cacheIndex.entries()) {
        if (now - entry.createTime > CACHE_EXPIRE_MS) {
          expiredKeys.push(key)
        }
      }

      for (const key of expiredKeys) {
        await this.removeCache(key)
      }

      if (expiredKeys.length > 0) {
        console.log('[MediaCache] Cleaned expired entries:', expiredKeys.length)
      }
    },

    async cleanupIfNeeded() {
      if (this.totalCacheSize < MAX_CACHE_SIZE) return

      const targetSize = MAX_CACHE_SIZE * 0.8
      const entries = [...this.cacheIndex.entries()]
        .sort((a, b) => a[1].accessTime - b[1].accessTime)

      let cleaned = 0
      while (this.totalCacheSize > targetSize && entries.length > 0) {
        const [key] = entries.shift()
        await this.removeCache(key)
        cleaned++
      }

      if (cleaned > 0) {
        console.log('[MediaCache] LRU cleanup:', cleaned, 'entries, new size:', this.formatSize(this.totalCacheSize))
      }
    },

    async removeCache(key) {
      const entry = this.cacheIndex.get(key)
      if (entry) {
        this.totalCacheSize = Math.max(0, this.totalCacheSize - (entry.size || 0))
        this.cacheIndex.delete(key)
      }
    },

    async debouncedSaveMeta() {
      if (this._saveTimer) {
        clearTimeout(this._saveTimer)
      }
      this._saveTimer = setTimeout(() => {
        this.saveMeta()
      }, 1000)
    },

    async saveMeta() {
      try {
        await localForage.setItem(CACHE_META_KEY, {
          entries: [...this.cacheIndex.entries()],
          totalSize: this.totalCacheSize
        })
      } catch (error) {
        console.error('[MediaCache] Save meta failed:', error)
      }
    },

    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    getCacheStats() {
      let imageCount = 0
      let videoCount = 0
      let coverCount = 0

      for (const key of this.cacheIndex.keys()) {
        if (key.startsWith(IMAGE_PREFIX)) imageCount++
        else if (key.startsWith(VIDEO_PREFIX)) videoCount++
        else if (key.startsWith(COVER_PREFIX)) coverCount++
      }

      return {
        totalSize: this.totalCacheSize,
        totalSizeFormatted: this.formatSize(this.totalCacheSize),
        imageCount,
        videoCount,
        coverCount,
        totalCount: this.cacheIndex.size
      }
    },

    async clearAllCache() {
      this.cacheIndex.clear()
      this.totalCacheSize = 0
      await localForage.removeItem(CACHE_META_KEY)
      console.log('[MediaCache] All cache cleared')
    }
  }
})
